<template>
  <div class="max-w-6xl mx-auto">
    <ProtexWrapper
      v-if="isIntegrationEnabled('protex')"
      :product-line-slug="data.productLineSlug"
      @has-application=";(renderProtex = $event), $event && $uiEvents.$emit('BuilderButtonViewed', data)"
    ></ProtexWrapper>
    <GalleryABTest :gallery="gallery" :show-thumbs="showThumbs">
      <template #protex>
        <!-- Slot won't exist if !renderProtex -->
        <div class="w-full">
          <ProtexIframe
            :base-url="`https://realtruck.oneprotex.com/preview/?focus=${data.productLineSlug}`"
            fit-div
          ></ProtexIframe>
        </div>
      </template>

      <template #thumbProtex="{ activateThumb, activeThumb, index }">
        <!-- Slot won't exist if  !renderProtex -->
        <button
          class="flex border border-solid rounded p-3 w-[92px] h-[92px] align-middle items-center justify-center"
          :class="[activeThumb === index ? 'border-black' : 'border-gray-light']"
          @click="activateThumb(index)"
        >
          <img
            src="/images/3dSpin.svg"
            alt="3d Spin Icon"
            width="48"
            height="32"
            class="w-full"
            @click="$uiEvents.$emit('BuilderButtonClicked', data)"
          />
        </button>
      </template>

      <template #thumbMorePhotos="{ activateThumb, loadLightbox, activeThumb, index, image }">
        <button
          class="relative border border-solid rounded w-[92px] h-[92px] align-middle items-center justify-center"
          :class="[activeThumb === index ? 'border-black' : 'border-gray-light']"
          @click="activateThumb(index), loadLightbox(image.lightboxIndex)"
        >
          <img
            :src="getImageUrl(image.key, image.filename, 92, 92, 70) || ''"
            alt="More Photos"
            width="92"
            height="92"
            class="w-full h-full"
          />
          <span class="bg-black opacity-75 inset-0 absolute"></span>
          <span class="absolute inset-0 flex items-center justify-center">
            <span class="text-white text-2xl font-bold text-center"> {{ hiddenThumbnailsCount }}+</span>
          </span>
        </button>
      </template>

      <template #thumbVideo="{ image }">
        <button
          class="relative border border-solid rounded w-[92px] h-[92px] align-middle items-center justify-center"
          @click="scrollToTarget('scrollTarget-videos')"
        >
          <img :src="image.filename" alt="Videos" width="92" height="92" class="w-full h-full" />
          <span class="absolute inset-0 flex items-center justify-center">
            <span class="bg-black opacity-75 inset-0 absolute"></span>
            <img src="/images/play.svg" alt="play videos icon" width="30" height="30" class="z-10" />
          </span>
        </button>
      </template>

      <template #thumbCustomerPhotos>
        <button
          class="flex flex-col p-3 border border-black border-solid rounded w-[92px] h-[92px] space-y-0.5 align-middle items-center justify-center"
          @click="scrollToTarget('scrollTarget-photos')"
        >
          <img src="/images/customPhoto.svg" alt="customer photos" width="20" height="16" class="w-[20px]" />
          <span class="text-[10px] text-center font-bold leading-3"> Customer Photos</span>
        </button>
      </template>

      <template #default="{ activateThumb }">
        <div class="flex flex-col mt-4 lg:flex-row lg:mt-0">
          <template v-if="renderProtex && $device.isMobile">
            <button
              type="button"
              class="flex items-center justify-center font-bold py-2 px-6 mb-2 lg:mr-2 lg:mb-0 border rounded text-inherit w-full"
              @click="activateThumb(protexSlotIndex), $uiEvents.$emit('BuilderButtonClicked', data)"
            >
              <img src="/images/3dSpin.svg" alt="3d spin icon" width="24" height="16" class="w-6 h-4 mr-2" />
              <span>See it on your {{ fitmentStore.model }}</span>
            </button>
          </template>

          <button
            v-if="hasCustomerPhotos && $device.isMobile"
            type="button"
            class="flex items-center justify-center font-bold py-2 px-6 border rounded"
            @click="scrollToTarget('scrollTarget-photos')"
          >
            <img
              src="/images/customPhoto.svg"
              alt="customer photos icon"
              width="18"
              height="14"
              class="w-[18px] h-3.5 mr-2"
            />
            <span>Customer Photos</span>
          </button>
        </div>
      </template>
    </GalleryABTest>
  </div>
</template>

<script setup lang="ts">
const { $uiEvents, $device } = useNuxtApp()
const { $scrollTo } = useNuxtApp()
const { isIntegrationEnabled } = useUtils()
const { getImageUrl } = useUrls()

const fitmentStore = useFitmentStore()
const renderProtex = ref(false)
const { data, showThumbs, hasCustomerPhotos } = defineProps<{
  data: ProductLine
  showThumbs?: boolean
  hasCustomerPhotos: boolean
}>()

const hiddenThumbnailsCount = ref(0)

function scrollToTarget(target: string) {
  const element = document.getElementById(target)
  if (element) $scrollTo(element, 500)
}

const protexSlotIndex = 1
const gallery = computed(() => {
  const gallery = [...data.gallery]

  // Add the Protex Slots to the gallery
  if (isIntegrationEnabled('protex') && renderProtex.value) {
    gallery.splice(protexSlotIndex, 0, {
      gallerySlot: 'protex',
      thumbSlot: 'thumbProtex',
      filename: '',
      key: 'protex',
      title: 'View in Protex',
    })
  }

  // Add video thumbnail to the end if videos exist
  if (data.videos?.videos && data.videos.videos.length > 0 && data.videos.videos[0].thumbnail?.key) {
    gallery.push({
      gallerySlot: 'video',
      thumbSlot: 'thumbVideo',
      filename:
        getImageUrl(data.videos.videos[0].thumbnail.key, data.videos.videos[0].thumbnail.filename, 92, 92) || '',
      key: 'video',
      title: 'Videos',
    })
  }

  // Add customer photos thumbnail to the end if customer photos exist
  if (hasCustomerPhotos) {
    gallery.push({
      gallerySlot: 'customerPhotos',
      thumbSlot: 'thumbCustomerPhotos',
      filename: '',
      key: 'customerPhotos',
      title: 'Customer Photos',
    })
  }

  // If there are more than 8 images we want to hide the remainder photo thumbnails
  if (gallery.length > 8) {
    const protexItem = gallery.find((item) => item.key === 'protex')
    const videoItem = gallery.find((item) => item.key === 'video')
    const customerPhotosItem = gallery.find((item) => item.key === 'customerPhotos')

    let lastIndex = 7
    if (videoItem) lastIndex -= 1
    if (customerPhotosItem) lastIndex -= 1

    // Create a thumb slot for the more photos button
    gallery[lastIndex].thumbSlot = 'thumbMorePhotos'
    // We need to offset the index of the lightbox item if protex is in the gallery
    gallery[lastIndex].lightboxIndex = protexItem ? lastIndex - 1 : lastIndex

    // For the rest of the gallery items hide the thumbnail if its not a video, customer photos, or protex
    for (let i = lastIndex + 1; i < gallery.length; i++) {
      if (gallery[i].key === 'protex' || gallery[i].key === 'video' || gallery[i].key === 'customerPhotos') continue
      gallery[i].hideThumb = true
    }

    // Count the number of hidden thumbnails including the more photos button
    hiddenThumbnailsCount.value = gallery.filter((item) => item.hideThumb).length + 1
  }

  return gallery
})
</script>
